/** Visual tokens for cost/store panels — admin panel vs store page × dark/light. */

const ADMIN_DARK = {
  paperBg: '#152033',
  border: '1px solid #2f3a46',
  titleColor: '#90a4ae',
  subtitleColor: '#78909c',
  amountColor: '#ffffff',
  lineLabelColor: '#b0bec5',
  lineValueColor: '#eceff1',
  dividerColor: '#2f3a46',
  disclaimerColor: '#607d8b',
  cartBg: 'rgba(255, 255, 255, 0.04)',
  cartBorder: '1px solid #37474f',
  buttonColor: '#ffffff',
  buttonBorder: '#546e7a',
  textPrimary: '#ffffff',
  textSecondary: '#b0bec5',
  pageBg: '#0a1a2f',
  appBarBg: '#0f1c30',
  cardBg: '#1a2332',
  chipText: '#ffffff',
};

const STORE_DARK = {
  ...ADMIN_DARK,
  paperBg: '#1a2332',
  titleColor: '#b0bec5',
  subtitleColor: '#90a4ae',
  lineLabelColor: '#cfd8dc',
  disclaimerColor: '#78909c',
  cartBg: 'rgba(76, 175, 80, 0.08)',
  cartBorder: '1px solid rgba(76, 175, 80, 0.25)',
};

const ADMIN_LIGHT = {
  paperBg: '#f8fafc',
  border: '1px solid #cbd5e1',
  titleColor: '#334155',
  subtitleColor: '#334155',
  amountColor: '#0f172a',
  lineLabelColor: '#1e293b',
  lineValueColor: '#0f172a',
  dividerColor: '#cbd5e1',
  disclaimerColor: '#475569',
  cartBg: 'rgba(16, 185, 129, 0.08)',
  cartBorder: '1px solid rgba(16, 185, 129, 0.25)',
  buttonColor: '#0f172a',
  buttonBorder: '#64748b',
  textPrimary: '#0f172a',
  textSecondary: '#334155',
  pageBg: '#e2e8f0',
  appBarBg: '#ffffff',
  cardBg: '#ffffff',
  chipText: '#ffffff',
};

const STORE_LIGHT = {
  paperBg: '#ffffff',
  border: '1px solid #cbd5e1',
  titleColor: '#334155',
  subtitleColor: '#334155',
  amountColor: '#0f172a',
  lineLabelColor: '#1e293b',
  lineValueColor: '#0f172a',
  dividerColor: '#cbd5e1',
  disclaimerColor: '#475569',
  cartBg: 'rgba(16, 185, 129, 0.08)',
  cartBorder: '1px solid rgba(16, 185, 129, 0.25)',
  buttonColor: '#0f172a',
  buttonBorder: '#64748b',
  textPrimary: '#0f172a',
  textSecondary: '#334155',
  pageBg: '#e2e8f0',
  appBarBg: '#ffffff',
  cardBg: '#ffffff',
  chipText: '#ffffff',
};

export const STORE_THEME_STORAGE_KEY = 'webscada.storePanelTheme';

export function getCostPanelTheme(context = 'store', mode = 'dark') {
  if (context === 'admin') {
    return mode === 'light' ? ADMIN_LIGHT : ADMIN_DARK;
  }
  return mode === 'light' ? STORE_LIGHT : STORE_DARK;
}

const ANIMATION_DARK = {
  frameGradient: 'linear-gradient(145deg, #0a1628 0%, #132238 50%, #0d1a2e 100%)',
  frameBorder: '1px solid rgba(33, 150, 243, 0.25)',
  gridLine: '#1e3a5f',
  chartLine: '#42a5f5',
  chartFill: '#42a5f5',
  chartDot: '#81d4fa',
  phoneBorder: '#2a3f5f',
  phoneBg: '#0d1520',
  bubbleIncoming: '#37474f',
  bubbleOutgoing: '#0088cc',
  hmiTankFill: '#1a237e',
  hmiTankStroke: '#5c6bc0',
  hmiBoxFill: '#263238',
  hmiBoxStroke: '#78909c',
  hmiPumpFill: '#1b5e20',
  hmiPumpStroke: '#66bb6a',
  hmiPipe: '#ff9800',
  hmiLabelA: '#90caf9',
  hmiLabelB: '#a5d6a7',
  hmiValue: '#ffffff',
  tagBg: 'rgba(33, 150, 243, 0.15)',
  tagBorder: '1px solid rgba(33, 150, 243, 0.4)',
  tagText: '#90caf9',
  alarmBannerBg: 'rgba(244, 67, 54, 0.15)',
  alarmBannerBorder: '1px solid rgba(244, 67, 54, 0.4)',
  alarmBannerText: '#ffcdd2',
  cmdActiveBg: 'rgba(76, 175, 80, 0.2)',
  cmdIdleBg: 'rgba(255,255,255,0.06)',
  cmdBorder: '1px solid rgba(255,255,255,0.12)',
  cmdText: '#ffffff',
  cmdMeta: '#90a4ae',
  mapBg: '#132238',
  mapRiverA: '#2a4568',
  mapRiverB: '#1e3a5f',
  mapPin: '#ef5350',
  mapPinHead: '#f44336',
  debugText: '#81c784',
  genericText: '#546e7a',
};

const ANIMATION_LIGHT = {
  frameGradient: 'linear-gradient(145deg, #e2e8f0 0%, #f8fafc 50%, #eef2f7 100%)',
  frameBorder: '1px solid rgba(30, 64, 175, 0.22)',
  gridLine: '#cbd5e1',
  chartLine: '#1d4ed8',
  chartFill: '#3b82f6',
  chartDot: '#2563eb',
  phoneBorder: '#94a3b8',
  phoneBg: '#ffffff',
  bubbleIncoming: '#e2e8f0',
  bubbleOutgoing: '#0284c7',
  hmiTankFill: '#dbeafe',
  hmiTankStroke: '#3b82f6',
  hmiBoxFill: '#f1f5f9',
  hmiBoxStroke: '#64748b',
  hmiPumpFill: '#dcfce7',
  hmiPumpStroke: '#16a34a',
  hmiPipe: '#ea580c',
  hmiLabelA: '#1d4ed8',
  hmiLabelB: '#15803d',
  hmiValue: '#0f172a',
  tagBg: 'rgba(37, 99, 235, 0.1)',
  tagBorder: '1px solid rgba(37, 99, 235, 0.35)',
  tagText: '#1e40af',
  alarmBannerBg: 'rgba(239, 68, 68, 0.12)',
  alarmBannerBorder: '1px solid rgba(239, 68, 68, 0.35)',
  alarmBannerText: '#b91c1c',
  cmdActiveBg: 'rgba(22, 163, 74, 0.15)',
  cmdIdleBg: 'rgba(15, 23, 42, 0.04)',
  cmdBorder: '1px solid rgba(15, 23, 42, 0.12)',
  cmdText: '#0f172a',
  cmdMeta: '#475569',
  mapBg: '#eef2f7',
  mapRiverA: '#94a3b8',
  mapRiverB: '#cbd5e1',
  mapPin: '#ef4444',
  mapPinHead: '#dc2626',
  debugText: '#15803d',
  genericText: '#64748b',
};

export function getAnimationTheme(mode = 'dark') {
  return mode === 'light' ? ANIMATION_LIGHT : ANIMATION_DARK;
}

export function getInitialStoreThemeMode() {
  try {
    const saved = localStorage.getItem(STORE_THEME_STORAGE_KEY);
    return saved === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export function saveStoreThemeMode(mode) {
  try {
    localStorage.setItem(STORE_THEME_STORAGE_KEY, mode);
  } catch {
    // ignore
  }
}

export function getStatusChipSx(status, ui, themeMode = 'dark') {
  const isLight = themeMode === 'light';
  const label = (lightColor, darkColor = ui.chipText) => (isLight ? lightColor : darkColor);
  const base = (textColor) => ({
    color: textColor,
    fontWeight: 600,
    '& .MuiChip-label': { color: textColor },
    '& .MuiChip-icon': { color: textColor },
  });
  if (status === 'connected') {
    const c = label('#166534', '#ffffff');
    return {
      ...base(c),
      borderColor: '#43a047',
      bgcolor: isLight ? 'rgba(22, 163, 74, 0.12)' : 'rgba(67, 160, 71, 0.25)',
    };
  }
  if (status === 'inPlan') {
    const c = label('#334155', ui.chipText);
    return {
      ...base(c),
      borderColor: isLight ? '#64748b' : '#546e7a',
      bgcolor: isLight ? 'rgba(100, 116, 139, 0.12)' : 'rgba(84, 110, 122, 0.35)',
    };
  }
  const c = label('#475569', ui.chipText);
  return {
    ...base(c),
    borderColor: isLight ? '#94a3b8' : '#78909c',
    bgcolor: isLight ? 'rgba(71, 85, 105, 0.1)' : 'rgba(120, 144, 156, 0.25)',
  };
}
